import type { FontSpec } from "chart.js";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
} from "chart.js";

import { ColorConstants } from "../../../constants";

const SPACE_GROTESK = "'Space Grotesk', sans-serif";
const FIGTREE = "'Figtree', sans-serif";

/** The product's primary typeface — the default (Hub) chart typeface. */
export const FONT_FAMILY = SPACE_GROTESK;

/**
 * Design-system brand for a chart. Mirrors the `variant` on KanbanBoard / the `font`
 * on DiscussionsPanel: **Hub** = Space Grotesk + square corners; **Seed** = Figtree +
 * rounded corners. Defaults to `"hub"`; Seed contexts (e.g. DM / AM roles) opt in.
 */
export type ChartVariant = "hub" | "seed";

/** Resolved styling tokens for a brand variant. Consumed by every chart component/hook. */
export interface ChartTheme {
  variant: ChartVariant;
  /** Typeface for all chart text (axes, legend, tooltip). */
  fontFamily: string;
  /** Radius on bar tops (px). */
  barBorderRadius: number;
  /** Doughnut/pie segment corner radius (px). */
  arcBorderRadius: number;
  /** Line curve smoothing (Chart.js tension, 0–1). */
  lineTension: number;
  /** Tooltip card corner radius (px). */
  tooltipCornerRadius: number;
  /** Whether legend keys render as a point style rather than a filled box. */
  legendUsePointStyle: boolean;
  /** Point style used when `legendUsePointStyle` is true. */
  legendPointStyle: "circle" | "rect" | "rectRounded";
  /** Corner radius for the pie chart's HTML legend swatches (px). */
  legendKeyBorderRadius: number;
  /** Typeface for the donut's centre figure: Hub keeps its own, Seed uses Fraunces. */
  centerTextFont: "Space Grotesk" | "Fraunces";
}

const HUB_THEME: ChartTheme = {
  variant: "hub",
  fontFamily: SPACE_GROTESK,
  barBorderRadius: 0,
  arcBorderRadius: 0,
  lineTension: 0.2,
  tooltipCornerRadius: 0,
  legendUsePointStyle: false,
  legendPointStyle: "rect",
  legendKeyBorderRadius: 2,
  centerTextFont: "Space Grotesk",
};

const SEED_THEME: ChartTheme = {
  variant: "seed",
  fontFamily: FIGTREE,
  barBorderRadius: 6,
  arcBorderRadius: 4,
  lineTension: 0.35,
  tooltipCornerRadius: 8,
  legendUsePointStyle: true,
  legendPointStyle: "rectRounded",
  /* A rounded square, not a dot: 6 on a 12px swatch is a circle, which read as a
     different kind of mark from the rounded arcs it stands for — and contradicted the
     rectRounded point style declared right above. */
  legendKeyBorderRadius: 4,
  centerTextFont: "Fraunces",
};

/** Resolves a brand variant to its concrete styling tokens. Defaults to Hub. */
export const getChartTheme = (variant: ChartVariant = "hub"): ChartTheme =>
  variant === "seed" ? SEED_THEME : HUB_THEME;

/** Builds a Chart.js `FontSpec` fragment in the theme's typeface. */
export const themeFont = (theme: ChartTheme, extra?: Partial<FontSpec>): Partial<FontSpec> => ({
  family: theme.fontFamily,
  ...extra,
});

/**
 * Normalises a colour into an `"r, g, b"` triplet suitable for interpolating into
 * `rgb(...)` / `rgba(..., alpha)` strings (used by the line-chart area gradients).
 * Accepts hex (`#28245c`, `#abc`) or an existing `"r, g, b"` triplet, which passes through.
 */
export const toRgbTriplet = (color: string | undefined): string => {
  // Fall back to the neutral grey triplet when a dataset has no matching colour
  // (e.g. more series than entries in the colour list).
  const trimmed = (color ?? ColorConstants.GREY_500_RGB).trim();

  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);
    if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c);
    const int = parseInt(hex, 16);
    // eslint-disable-next-line no-bitwise
    return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
  }

  // Already an "r, g, b" (or "r,g,b") triplet.
  return trimmed;
};

let hasChartJsGlobalConfigurationBeenSet = false;

export const maybeSetGlobalChartJsConfiguration = (): void => {
  if (hasChartJsGlobalConfigurationBeenSet) return;

  ChartJS.register(
    RadialLinearScale,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
  );

  ChartJS.defaults.responsive = true;
  ChartJS.defaults.normalized = true;
  ChartJS.defaults.font.size = 12;
  ChartJS.defaults.font.family = FONT_FAMILY;
  ChartJS.defaults.color = ColorConstants.GREY_500;
  ChartJS.defaults.backgroundColor = ColorConstants.INDIGO_500;

  // Refined tooltip: a floating rounded card rather than a hard-edged box.
  ChartJS.defaults.plugins.tooltip.backgroundColor = ColorConstants.WHITE;
  ChartJS.defaults.plugins.tooltip.titleColor = ColorConstants.GREY_500;
  ChartJS.defaults.plugins.tooltip.bodyColor = ColorConstants.GREY_500;
  ChartJS.defaults.plugins.tooltip.titleFont = { family: FONT_FAMILY, size: 12, weight: 600 };
  ChartJS.defaults.plugins.tooltip.bodyFont = { family: FONT_FAMILY, size: 12, weight: 400 };
  ChartJS.defaults.plugins.tooltip.titleMarginBottom = 6;
  // Corner radius is brand-driven (Hub square / Seed rounded); each chart sets it from its
  // theme. This global value is only the Hub fallback.
  ChartJS.defaults.plugins.tooltip.cornerRadius = 0;
  ChartJS.defaults.plugins.tooltip.borderColor = ColorConstants.INDIGO_50;
  ChartJS.defaults.plugins.tooltip.borderWidth = 1;
  ChartJS.defaults.plugins.tooltip.padding = {
    top: 12,
    left: 14,
    right: 14,
    bottom: 12,
  };
  // Round pill swatches inside the tooltip instead of hard squares.
  ChartJS.defaults.plugins.tooltip.usePointStyle = true;
  ChartJS.defaults.plugins.tooltip.boxPadding = 6;
  ChartJS.defaults.plugins.tooltip.caretSize = 0;
  ChartJS.defaults.plugins.tooltip.xAlign = "center";
  ChartJS.defaults.plugins.tooltip.yAlign = "top";

  hasChartJsGlobalConfigurationBeenSet = true;
};

const getColours = (dataPointCount: number, colours: string[]): string[] => {
  const colourCount = colours.length;
  const ret = Array<string>(dataPointCount);
  for (let i = 0; i < dataPointCount; i++) {
    ret[i] = colours[i % colourCount];
  }
  return ret;
};

const getGradientColours = (dataPointCount: number, colours: string[][]): string[][] => {
  const colourCount = colours.length;
  const ret = Array<string[]>(dataPointCount);
  for (let i = 0; i < dataPointCount; i++) {
    ret[i] = colours[i % colourCount];
  }
  return ret;
};

export const getChartColours = (dataPointCount: number, colours?: string): string[] => {
  switch (colours) {
    case "roadmap":
      return getColours(dataPointCount, ColorConstants.CHART_ROADMAP_COLORS);
    case "verifierDashboard":
      return getColours(dataPointCount, ColorConstants.CHART_COLORS_VVB);
    case "discussionsKPI":
      return getColours(dataPointCount, ColorConstants.CHART_COLORS_DISCUSSIONS);
    case "default":
    default:
      return getColours(dataPointCount, ColorConstants.CHART_COLORS);
  }
};

export const getChartHighlightColours = (dataPointCount: number, colours?: string): string[] => {
  switch (colours) {
    case "roadmap":
      return getColours(dataPointCount, ColorConstants.CHART_ROADMAP_HIGHLIGHT_COLORS);
    case "verifierDashboard":
      return getColours(dataPointCount, ColorConstants.CHART_HIGHLIGHT_COLORS_VVB);
    case "discussionsKPI":
      return getColours(dataPointCount, ColorConstants.CHART_HIGHLIGHT_COLORS_DISCUSSIONS);
    case "default":
    default:
      return getColours(dataPointCount, ColorConstants.CHART_HIGHLIGHT_COLORS);
  }
};

export const getChartGradientColours = (dataPointCount: number, colours?: string): string[][] => {
  switch (colours) {
    case "roadmap":
      return getGradientColours(dataPointCount, ColorConstants.CHART_GRADIENT_ROADMAP_COLORS);
    case "default":
    default:
      return getGradientColours(dataPointCount, ColorConstants.CHART_GRADIENT_COLORS);
  }
};

export const getChartGradientHighlightColours = (dataPointCount: number, colours?: string): string[] => {
  switch (colours) {
    case "roadmap":
      return getColours(dataPointCount, ColorConstants.CHART_ROADMAP_HIGHLIGHT_COLORS);
    case "default":
    default:
      return getColours(dataPointCount, ColorConstants.CHART_GRADIENT_HIGHLIGHT_COLORS);
  }
};
